import { createSlice } from "@reduxjs/toolkit";

import { TestEventsListType } from "../../../enums/test-events";
import {
    deleteTrackerTestEventCodeAsync,
    getTestEventDetailAsync,
    getTestEventsOrRequestsAsync,
    getTrackerTestEventCodeAsync,
    postTrackerTestEventCodeAsync,
    putTrackerTestEventCodeAsync,
} from "./testEvents.asyncActions";

const initialState = {
    eventsList: {
        isListLoading: null,
        isError: false,
        data: [],
        page: 0,
        pages: null,
        limit: 10,
        totalCount: null,
    },
    requestsList: {
        isListLoading: null,
        isError: false,
        data: [],
        page: 0,
        pages: null,
        limit: 10,
        totalCount: null,
    },
    activeTab: TestEventsListType.EVENTS,
    trackerTestEventCode: {
        isLoading: null,
        isSaving: false,
        isUpdating: false,
        isDeleting: false,
        data: undefined,
    },
    eventDetails: {
        data: null,
        isLoading: null,
    },
    trackerId: null,
};

const testEventsSlice = createSlice({
    name: "testEvents",
    initialState,
    reducers: {
        nextPage: (state) => {
            const willBeUpdatedKey =
                state.activeTab === TestEventsListType.EVENTS
                    ? "eventsList"
                    : "requestsList";
            const list = state[willBeUpdatedKey];

            if (list.pages > list.page) state[willBeUpdatedKey].page += 1;
        },
        prevPage: (state) => {
            const willBeUpdatedKey =
                state.activeTab === TestEventsListType.EVENTS
                    ? "eventsList"
                    : "requestsList";
            const list = state[willBeUpdatedKey];

            if (list.page !== 0) state[willBeUpdatedKey].page -= 1;
        },
        selectPage: (state, action) => {
            const willBeUpdatedKey =
                state.activeTab === TestEventsListType.EVENTS
                    ? "eventsList"
                    : "requestsList";
            state[willBeUpdatedKey].page = action.payload;
        },

        setActiveTab: (state, action) => {
            state.activeTab = action.payload;
            const newTabKey =
                state.activeTab === TestEventsListType.EVENTS
                    ? "eventsList"
                    : "requestsList";

            state[newTabKey].page = 0;
            return state;
        },

        setTrackerId: (state, action) => {
            state.trackerId = action.payload;
        },
        clearState: (state) => {
            state = initialState;
            return state;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getTestEventsOrRequestsAsync.pending, (state) => {
                const willBeUpdatedKey =
                    state.activeTab === TestEventsListType.EVENTS
                        ? "eventsList"
                        : "requestsList";
                state[willBeUpdatedKey].isListLoading = true;
                state[willBeUpdatedKey].isError = false;
            })
            .addCase(
                getTestEventsOrRequestsAsync.fulfilled,
                (state, action) => {
                    const willBeUpdatedKey =
                        state.activeTab === TestEventsListType.EVENTS
                            ? "eventsList"
                            : "requestsList";
                    const list = state[willBeUpdatedKey];

                    list.isListLoading = false;
                    list.isError = !action.payload;
                    list.data = action.payload?.response;
                    list.totalCount = action.payload?.total_count;
                    list.pages = Math.ceil(
                        action.payload?.total_count / list.limit,
                    );
                },
            )
            .addCase(getTestEventsOrRequestsAsync.rejected, (state) => {
                const willBeUpdatedKey =
                    state.activeTab === TestEventsListType.EVENTS
                        ? "eventsList"
                        : "requestsList";
                state[willBeUpdatedKey].isListLoading = false;
                state[willBeUpdatedKey].isError = true;
            });

        builder
            .addCase(getTestEventDetailAsync.pending, (state) => {
                state.eventDetails.isLoading = true;
            })
            .addCase(getTestEventDetailAsync.fulfilled, (state, action) => {
                state.eventDetails.isLoading = false;
                state.eventDetails.data = action.payload;
            })
            .addCase(getTestEventDetailAsync.rejected, (state) => {
                state.eventDetails.isLoading = false;
            });

        builder
            .addCase(putTrackerTestEventCodeAsync.pending, (state) => {
                state.trackerTestEventCode.isUpdating = true;
            })
            .addCase(putTrackerTestEventCodeAsync.fulfilled, (state) => {
                state.trackerTestEventCode.isUpdating = false;
            })
            .addCase(putTrackerTestEventCodeAsync.rejected, (state) => {
                state.trackerTestEventCode.isUpdating = false;
            });

        builder
            .addCase(postTrackerTestEventCodeAsync.pending, (state) => {
                state.trackerTestEventCode.isSaving = true;
            })
            .addCase(postTrackerTestEventCodeAsync.fulfilled, (state) => {
                state.trackerTestEventCode.isSaving = false;
            })
            .addCase(postTrackerTestEventCodeAsync.rejected, (state) => {
                state.trackerTestEventCode.isSaving = false;
            });

        builder
            .addCase(deleteTrackerTestEventCodeAsync.pending, (state) => {
                state.trackerTestEventCode.isDeleting = true;
            })
            .addCase(deleteTrackerTestEventCodeAsync.fulfilled, (state) => {
                state.trackerTestEventCode.isDeleting = false;
            })
            .addCase(deleteTrackerTestEventCodeAsync.rejected, (state) => {
                state.trackerTestEventCode.isDeleting = false;
            });

        builder
            .addCase(getTrackerTestEventCodeAsync.pending, (state) => {
                state.trackerTestEventCode.isLoading = true;
            })
            .addCase(
                getTrackerTestEventCodeAsync.fulfilled,
                (state, action) => {
                    state.trackerTestEventCode.data = action.payload;
                    state.trackerTestEventCode.isLoading = false;
                },
            )
            .addCase(getTrackerTestEventCodeAsync.rejected, (state) => {
                state.trackerTestEventCode.isLoading = false;
            });
    },
});

export default testEventsSlice.reducer;

export const {
    nextPage,
    prevPage,
    selectPage,
    setActiveTab,
    setTrackerId,
    clearState,
} = testEventsSlice.actions;
